<script setup lang="ts">
import { intitializeFilters, type SuperCarouselItem } from '@laam/cms-shared';
import { useBrands } from '~/data/brands';
import { useDrops } from '~/data/drops';
import { useProducts } from '~/data/products';

interface Props {
	entity: string;
	identifier: string;
	sectionIndex: number;
	data: SuperCarouselItem;
}

const { entity, data } = defineProps<Props>();

const isProductsEnabled = computed(
	() =>
		entity === 'products' &&
		(data.output === null || data.output === undefined),
);
const isBrandsEnabled = computed(
	() =>
		entity === 'brands' && (data.output === null || data.output === undefined),
);
const isDropsEnabled = computed(
	() =>
		entity === 'drops' && (data.output === null || data.output === undefined),
);
const filters = computed(() => {
	return intitializeFilters(
		{
			alignment: 'left',
			entity: data.entity,
			input: data.input,
			method: 'dynamic',
			title: '',
		},
		8,
	);
});

const {
	data: products,
	isLoading: areProductsLoading,
	viewMoreLink,
} = useProducts(filters, isProductsEnabled);

const { data: dropsData, isLoading: areDropsLoading } = useDrops({
	enabled: isDropsEnabled,
	filters,
});
const drops = computed(() => {
	if (Array.isArray(dropsData.value)) {
		return dropsData.value;
	}
	return dropsData.value?.drops || [];
});
const { data: brands, isLoading: areBrandsLoading } = useBrands({
	enabled: isBrandsEnabled,
	filters,
});

const isLoading = computed(() => {
	return (
		(isProductsEnabled && areProductsLoading.value) ||
		(isBrandsEnabled && areBrandsLoading.value) ||
		(isDropsEnabled && areDropsLoading.value)
	);
});

const output = computed(() => {
	if (isProductsEnabled.value) {
		return products.value;
	} else if (isBrandsEnabled.value) {
		return brands.value;
	} else if (isDropsEnabled.value) {
		return drops.value;
	} else if (data.output !== null) {
		return data.output;
	}
	return [];
});
</script>
<template>
	<slot
		:data="output"
		:is-loading="isLoading"
		:view-more-link="viewMoreLink"
	></slot>
</template>
